import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useShop } from '../contexts/ShopContext';
import { useToast } from '../contexts/ToastContext';
import { Address, BillingAddress } from '../lib/providerTypes';

interface BusinessInfo {
  store_shipping_address: Address;
  store_billing_address: BillingAddress;
}

const emptyShippingAddress: Address = {
  name: '',
  street: '',
  city: '',
  zip_code: '',
  country: '',
  phone_number: '',
  email: '',
};

const emptyBillingAddress: BillingAddress = {
  company_name: '',
  vat_id: '',
  street: '',
  city: '',
  zip_code: '',
  country: '',
};

const MerchantSettings: React.FC = () => {
  const { currentShopId } = useShop();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [shippingAddress, setShippingAddress] = useState<Address>({ ...emptyShippingAddress });
  const [billingAddress, setBillingAddress] = useState<BillingAddress>({ ...emptyBillingAddress });

  useEffect(() => {
    const loadBusinessInfo = async () => {
      if (!currentShopId) {
        setLoading(false);
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .from('merchant_config')
          .select('business_info')
          .eq('merchant_id', currentShopId)
          .maybeSingle();

        if (fetchError) throw fetchError;

        if (data?.business_info) {
          const info = data.business_info as BusinessInfo;
          if (info.store_shipping_address) {
            setShippingAddress({ ...emptyShippingAddress, ...info.store_shipping_address });
          }
          if (info.store_billing_address) {
            setBillingAddress({ ...emptyBillingAddress, ...info.store_billing_address });
          }
        }
      } catch (err) {
        console.error('Error loading business info:', err);
        toast.error('Failed to load business info');
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    loadBusinessInfo();
  }, [currentShopId]);

  const updateShipping = (field: keyof Address, value: string) => {
    setShippingAddress((prev) => ({ ...prev, [field]: value }));
  };

  const updateBilling = (field: keyof BillingAddress, value: string) => {
    setBillingAddress((prev) => ({ ...prev, [field]: value }));
  };

  const validate = (): boolean => {
    if (!shippingAddress.name || !shippingAddress.street || !shippingAddress.city ||
        !shippingAddress.zip_code || !shippingAddress.country || !shippingAddress.phone_number) {
      toast.error('Please fill all required shipping address fields');
      return false;
    }
    if (!billingAddress.company_name || !billingAddress.street || !billingAddress.city ||
        !billingAddress.zip_code || !billingAddress.country) {
      toast.error('Please fill all required billing address fields');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!currentShopId) return;
    if (!validate()) return;

    setSaving(true);

    try {
      const businessInfo: BusinessInfo = {
        store_shipping_address: shippingAddress,
        store_billing_address: billingAddress,
      };

      const { error: rpcError } = await supabase.rpc('upsert_merchant_business_info', {
        p_merchant_id: currentShopId,
        p_business_info: businessInfo,
      });

      if (rpcError) throw rpcError;
      toast.success('Business info saved successfully');
    } catch (err) {
      console.error('Error saving business info:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to save business info');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/40 text-sm">Loading settings...</p>
        </div>
      </div>
    );
  }

  if (!currentShopId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-white/40 text-sm">No shop selected</p>
      </div>
    );
  }

  const inputClass = "w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] rounded-lg text-white text-sm font-mono placeholder-white/20 focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/20 disabled:opacity-40";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h2 className="font-heading font-bold text-white text-2xl">Business Info</h2>
        <p className="text-white/40 text-sm mt-1">
          Manage your shipping and billing details. This info is used to prefill provider connection forms.
        </p>
      </div>

      {/* Shipping Address */}
      <div className="glass-card rounded-2xl mb-6 overflow-hidden">
        <div className="px-6 py-4 border-b border-white/[0.06]">
          <h3 className="font-heading font-bold text-white text-base">Shipping Address</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs text-white/40 mb-1.5">Name *</label>
              <input type="text" value={shippingAddress.name} onChange={(e) => updateShipping('name', e.target.value)} className={inputClass} placeholder="Store name" disabled={saving} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-white/40 mb-1.5">Street *</label>
              <input type="text" value={shippingAddress.street} onChange={(e) => updateShipping('street', e.target.value)} className={inputClass} placeholder="Street address" disabled={saving} />
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1.5">City *</label>
              <input type="text" value={shippingAddress.city} onChange={(e) => updateShipping('city', e.target.value)} className={inputClass} disabled={saving} />
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1.5">Zip Code *</label>
              <input type="text" value={shippingAddress.zip_code} onChange={(e) => updateShipping('zip_code', e.target.value)} className={inputClass} disabled={saving} />
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1.5">Country Code *</label>
              <input type="text" value={shippingAddress.country} onChange={(e) => updateShipping('country', e.target.value.toUpperCase())} className={inputClass} placeholder="NL, US, FR..." maxLength={2} disabled={saving} />
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1.5">Phone *</label>
              <input type="tel" value={shippingAddress.phone_number} onChange={(e) => updateShipping('phone_number', e.target.value)} className={inputClass} placeholder="+31612345678" disabled={saving} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-white/40 mb-1.5">Email</label>
              <input type="email" value={shippingAddress.email || ''} onChange={(e) => updateShipping('email', e.target.value)} className={inputClass} placeholder="shipping@example.com" disabled={saving} />
              <p className="text-xs text-white/20 mt-1">Some providers require an email address</p>
            </div>
          </div>
        </div>
      </div>

      {/* Billing Address */}
      <div className="glass-card rounded-2xl mb-6 overflow-hidden">
        <div className="px-6 py-4 border-b border-white/[0.06]">
          <h3 className="font-heading font-bold text-white text-base">Billing Address</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs text-white/40 mb-1.5">Company Name *</label>
              <input type="text" value={billingAddress.company_name} onChange={(e) => updateBilling('company_name', e.target.value)} className={inputClass} disabled={saving} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-white/40 mb-1.5">VAT ID</label>
              <input type="text" value={billingAddress.vat_id || ''} onChange={(e) => updateBilling('vat_id', e.target.value)} className={inputClass} placeholder="NL123456789B01" disabled={saving} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-white/40 mb-1.5">Street *</label>
              <input type="text" value={billingAddress.street} onChange={(e) => updateBilling('street', e.target.value)} className={inputClass} disabled={saving} />
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1.5">City *</label>
              <input type="text" value={billingAddress.city} onChange={(e) => updateBilling('city', e.target.value)} className={inputClass} disabled={saving} />
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1.5">Zip Code *</label>
              <input type="text" value={billingAddress.zip_code} onChange={(e) => updateBilling('zip_code', e.target.value)} className={inputClass} disabled={saving} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-white/40 mb-1.5">Country Code *</label>
              <input type="text" value={billingAddress.country} onChange={(e) => updateBilling('country', e.target.value.toUpperCase())} className={inputClass} placeholder="NL, US, FR..." maxLength={2} disabled={saving} />
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 rounded-full bg-orange-500 hover:bg-orange-400 text-black text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Business Info'}
        </button>
      </div>
    </div>
  );
};

export default MerchantSettings;
